import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { dotPatternStyles } from '../../styles/backgrounds';
import { containerStyles } from '../../styles/layout';
import { body, headers, shortRichTextStyles } from '../../styles/type';

import ButtonGroup from '../ButtonGroup';
import Button from '../buttons/Button';
import ButtonText from '../buttons/ButtonText';
import Image from '../Image';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  padding: ${rem(48)} 0;
  overflow: hidden;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &::before {
    ${dotPatternStyles}
    content: '';
    position: absolute;
    z-index: -1;
    bottom: ${rem(-48)};
    left: ${rem(-4)};
    display: block;
    width: ${rem(90)};
    height: ${rem(112)};

    ${media('>small')} {
      width: ${rem(180)};
    }
  }
`;

const StyledImage = styled(Image)`
  width: ${rem(64)};
  height: ${rem(64)};
  margin: 0 auto ${rem(24)};
`;

const HeadlineH1 = styled.h1`
  ${headers.h2}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `};
`;

const HeadlineH2 = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `};
`;

const Subhead = styled.p`
  ${body.regular}
  margin-top: ${rem(12)};
  margin-bottom: ${rem(24)};
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `};

  ${media('>medium')} {
    margin-top: ${rem(20)};
  }
`;

const Text = styled.div`
  ${shortRichTextStyles}
  margin-top: ${rem(12)};
  margin-bottom: ${rem(24)};
  color: ${colors.gray['600']};

  ${media('>medium')} {
    margin-top: ${rem(20)};
  }
`;

const MiniHeader = ({
  useH1,
  image,
  headline,
  subhead,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  backgroundColor
}) => (
  <Container backgroundColor={backgroundColor}>
    <ContentWrapper>
      <StyledImage
        image={image}
        objectFit="contain"
        loading="eager"
      />
      {useH1 ? (
        <HeadlineH1>
          <TextRenderer text={headline} />
        </HeadlineH1>
      ) : (
        <HeadlineH2>
          <TextRenderer text={headline} />
        </HeadlineH2>
      )}
      {subhead && (
        <Subhead>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
      {text && (
        <Text>
          <TextRenderer text={text} richText />
        </Text>
      )}
      <ButtonGroup>
        {primaryCtaText && primaryCtaLink && (
          <Button to={primaryCtaLink}>
            <TextRenderer text={primaryCtaText} />
          </Button>
        )}
        {secondaryCtaText && secondaryCtaLink && (
          <ButtonText to={secondaryCtaLink}>
            <TextRenderer text={secondaryCtaText} />
          </ButtonText>
        )}
      </ButtonGroup>
    </ContentWrapper>
  </Container>
);

MiniHeader.propTypes = {
  useH1: PropTypes.bool,
  headline: PropTypes.string,
  subhead: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    references: PropTypes.arrayOf(PropTypes.object)
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment MiniHeaderFragment on ContentfulMiniHeader {
    id
    __typename
    image {
      gatsbyImageData(quality: 75)
      file {
        url
      }
      description
    }
    headline
    subhead
    text {
      raw
      references {
        ...TextRendererAssetFragment
        ...TextRendererButtonFragment
        ...TextRendererFormFragment
        ...TextRendererTrialFormFragment
        ...TextRendererImageFragment
        ...TextRendererMediaFragment
        ...TextRendererVideoFragment
        ...TextRendererWidgetFragment
      }
    }
    primaryCtaText,
    primaryCtaLink,
    secondaryCtaText,
    secondaryCtaLink,
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default MiniHeader;
